import React, { useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import fetch from 'cross-fetch';
import styles from './YourChildAgeElement.module.css';
import { ReactComponent as IntermediateIcon } from './assets/IntermediateIcon.svg';
import { ReactComponent as IntroductoryIcon } from './assets/IntroductoryIcon.svg';
import { AccountApiResponse, CommonPageStep, SignupPageStep } from '../..';
import { appConfig } from '../../../common/ConfigLoader';
import { useAppSelector } from '../../../store/hooks';
import {
  AppStateAuthentication,
  ChildProfile,
  setAppPageStep,
  updateAppAuthentication,
} from '../../../store/reducer/appStateSlice';
import {
  selectSignupData,
  setSignupData,
} from '../../../store/reducer/signupDataSlice';
import { outsideComponentDispatch } from '../../../store/store';

export enum SubscriptionLevelName {
  level1 = 'Introductory (for age 3-5)',
  level2 = 'Intermediate (for age 6-9)',
}

export interface YourChildAgeElementInterface {
  ageSelect: JSX.Element;
  contactUsButtonClickHandler: () => void;
  contactUsButtonMobile: JSX.Element;
  continueButton: JSX.Element;
  backButton: JSX.Element;
  backButtonClickHandler: () => void;
}

export const YourChildAgeElement = (): YourChildAgeElementInterface => {
  // Redux
  const signupData = useAppSelector(selectSignupData);

  // Handler
  const backButtonClickHandler = (): void => {
    console.debug('CLICKED BACK BUTTON');
    outsideComponentDispatch(
      setAppPageStep(SignupPageStep.CREATE_PARENT_PROFILE),
    );
  };

  const yourChildAgeContinueHandler = (): void => {
    const url = `${appConfig().accountServer}user/UserSignup`;
    const jsonData = {
      username: signupData.username,
      password: signupData.password,
      parentProfileData: {
        firstName: signupData.firstname,
        lastName: signupData.lastname,
        phoneNumber:
          signupData.phoneNumberCountryCode + signupData.phoneNumberNumber,
        numberOfChild: 1,
        email: signupData.username,
        accountAttributes: {
          isInitialized: false,
        },
      },
      childProfileData: {
        first: {
          firstName: 'Child',
          lastName: 'Child',
          gender: 'MALE',
          birthday: new Date().toISOString().split('T')[0],
          childLevel: signupData.childLevel,
        },
      },
    };

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(jsonData),
    })
      .then(async (res): Promise<void> => {
        const result = (await res.json()) as AccountApiResponse;
        if (result.success) {
          // Signup Successful
          // Login Immediately
          const url = `${appConfig().accountServer}user/UserLogin`;
          const jsonData = {
            username: signupData.username,
            password: signupData.password,
          };

          fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(jsonData),
          })
            .then(async (res): Promise<void> => {
              const result = (await res.json()) as AppStateAuthentication;
              if (result) {
                // Login Successful
                outsideComponentDispatch(
                  setAppPageStep(CommonPageStep.SUBSCRIPTION),
                );
                outsideComponentDispatch(updateAppAuthentication(result));
              }
            })
            .catch((err) => {
              console.debug('API ERROR', url, err);
            });
        }
      })
      .catch((err) => {
        console.debug('API ERROR', url, err);
      });
  };

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault();
        yourChildAgeContinueHandler();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return (): void => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  const setChildLevel = (
    childLevel: ChildProfile['child_profile_data']['childLevel'],
  ): void => {
    outsideComponentDispatch(
      setSignupData({ dataKey: 'childLevel', data: childLevel! }),
    );
  };

  return {
    // Handlers
    contactUsButtonClickHandler() {
      window.open(
        'https://api.whatsapp.com/send/?phone=85230015422&text&app_absent=0',
        '_blank',
      );
    },

    ageSelect: (
      <Box className={styles.levelCards}>
        <Box
          className={styles.levelCard}
          style={
            signupData.childLevel === 'Introductory'
              ? {
                  background: ' #fff3ed',
                  borderColor: '#ff8429',
                }
              : undefined
          }
          onClick={(): void => {
            setChildLevel('Introductory');
          }}
        >
          <IntroductoryIcon />
          <span className={styles.levelTitle}>Introductory Level</span>
          <span className={styles.levelSubtitle}>
            Recommended for ages <span className={styles.age}>4-5</span>
          </span>
        </Box>
        <Box
          className={styles.levelCard}
          style={
            signupData.childLevel === 'Intermediate'
              ? {
                  background: ' #fff3ed',
                  borderColor: '#ff8429',
                }
              : undefined
          }
          onClick={(): void => {
            setChildLevel('Intermediate');
          }}
        >
          <IntermediateIcon />
          <span className={styles.levelTitle}>Intermediate Level</span>
          <span className={styles.levelSubtitle}>
            Recommended for ages <span className={styles.age}>6-8</span>
          </span>
        </Box>
      </Box>
    ),
    continueButton: (
      <Button
        variant="contained"
        sx={{ width: 'inherite' }}
        fullWidth={true}
        onClick={yourChildAgeContinueHandler}
        disabled={signupData.childLevel === ''}
      >
        Continue
      </Button>
    ),
    backButton: (
      <Button
        variant="text"
        sx={{ width: 'inherite' }}
        fullWidth={true}
        onClick={backButtonClickHandler}
      >
        Back
      </Button>
    ),
    backButtonClickHandler,
    contactUsButtonMobile: (
      <Button
        variant="contained"
        fullWidth={true}
        sx={{ width: 'inherite' }}
        endIcon={
          <img
            id="whatsapp-icon"
            alt=""
            src="assets/common/whatsapp-icon.svg"
          />
        }
        onClick={(): void => {
          window.open(
            'https://api.whatsapp.com/send/?phone=85230015422&text&app_absent=0',
            '_blank',
          );
        }}
      >
        Contact Us
      </Button>
    ),
  };
};
